package br.com.vvrefeicoes.portal.core.network

import io.ktor.client.HttpClient
import io.ktor.client.HttpClientConfig
import io.ktor.client.plugins.HttpSend
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.plugin
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.request.header
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.serialization.kotlinx.json.json
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json

/**
 * Falha de rede/engine normalizada como [Exception], para que todo `catch (Exception)` a trate.
 * No Kotlin/JS/Wasm o erro `Failed to fetch` do browser chega como um `Throwable` que não é
 * `Exception`; aqui ele é embrulhado.
 */
class RemoteRequestException(
    cause: Throwable,
) : Exception(cause)

fun createHttpClient(
    refreshToken: (suspend (failedToken: String?) -> String?)? = null,
): HttpClient {
    val client = HttpClient { configureApi() }
    client.installAutomaticRefresh(refreshToken)
    return client
}

internal fun HttpClient.installAutomaticRefresh(
    refreshToken: (suspend (failedToken: String?) -> String?)? = null,
) {
    val refreshMutex = Mutex()
    plugin(HttpSend).intercept { request ->
        try {
            val call = execute(request)
            if (call.response.status != HttpStatusCode.Unauthorized || refreshToken == null) {
                return@intercept call
            }

            val failedToken = request.headers[HttpHeaders.Authorization]
            val newToken =
                refreshMutex.withLock {
                    refreshToken(failedToken)
                } ?: return@intercept call

            request.headers.remove(HttpHeaders.Authorization)
            request.header(HttpHeaders.Authorization, "Bearer $newToken")
            execute(request)
        } catch (exception: CancellationException) {
            throw exception
        } catch (throwable: Throwable) {
            throw RemoteRequestException(throwable)
        }
    }
}

fun HttpClientConfig<*>.configureApi() {
    install(ContentNegotiation) {
        json(Json { ignoreUnknownKeys = true })
    }
    install(WebSockets)
    install(HttpTimeout) {
        requestTimeoutMillis = 30_000
        connectTimeoutMillis = 10_000
        socketTimeoutMillis = 30_000
    }
}
